import * as React from 'react';
import {useDispatch} from 'react-redux';
import logger from 'src/utils/logger';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {useDashboardStore} from 'src/components/analytics-genesis/byod/store/dashboardStore';
import {useByodDateRange} from 'src/components/analytics-genesis/byod/hooks/useByodDateRange';
import {
  addTimezoneStartOfDay,
  addTimezoneEndOfDay,
} from '@spaced-out/ui-design-system/lib/utils';

import {baseRequest} from 'src/utils/redux-api-v2';
import {WIDGET_TYPE} from 'src/types/byod';
import type {
  DashboardQueryResponse,
  DateRange,
  PreviewResponse,
  QueryWidgetInput,
  WidgetConfig,
} from 'src/types/byod';

import {useInViewport} from 'src/hooks/useInViewport';
import {BYOD_API_BASE as BASE} from 'src/components/analytics-genesis/byod/api';
import {DEFAULT_PAGE_SIZE} from 'src/components/analytics-genesis/byod/constants';
import {
  BYODTileError,
  isByodErrorRetryable,
  reportByodApiError,
} from 'src/components/analytics-genesis/byod/api/error-handling';

/** Default number of automatic retries before the failure lock kicks in. */
const DEFAULT_TILE_RETRY = 1;

export type UseTileDataOptions = {
  /**
   * Number of automatic retries on transient errors before the tile locks
   * itself out of further auto-fetches. Defaults to 1 (so up to 2 total
   * attempts per fetch sequence).
   */
  retry?: number;
};

/**
 * True when the consuming tile is being rendered inside a drag overlay
 * (a transient, cursor-following clone) rather than its real canvas slot.
 * Read via `useContext(ByodDragOverlayContext)` so deep children don't
 * need a prop drilled through CanvasTile / ByodTable / ByodChart.
 *
 * Used by `useTileData` to suppress its viewport-driven fetch — the
 * overlay clone shares the placementId of an already-loaded tile, so any
 * fresh query would just re-hit `/query` for data already in the cache.
 */
const ByodDragOverlayContext = React.createContext(false);

export const ByodDragOverlayProvider = ByodDragOverlayContext.Provider;

/** Query key namespace — used for scoped invalidation (`['byod-tile']`). */
export const BYOD_TILE_QUERY_KEY = 'byod-tile';

export type UseTileDataResult = {
  /** Ref to attach to the tile wrapper — drives visibility tracking. */
  ref: (el: Element | null) => void;
  /** Widget data payload once loaded. */
  data: PreviewResponse | undefined;
  /** True from first enabled fetch until either success or error. */
  isLoading: boolean;
  /** True while a request is in flight (initial or refetch). */
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  /**
   * React Query's fetch lifecycle state. `'idle'` until the query is enabled
   * (tile has never scrolled into view); `'fetching'` while a request is in
   * flight; `'paused'` rarely. Used by tiles to decide between "never
   * fetched" skeleton and "actively loading" spinner.
   */
  fetchStatus: 'idle' | 'fetching' | 'paused';
  /** Manual retry (used by tile-level retry buttons). */
  refetch: () => void;
};

function buildWidgetInput(
  placementId: string,
  widget: WidgetConfig | undefined,
  dateRange: DateRange | null,
): QueryWidgetInput | null {
  if (!widget) {
    return null;
  }

  const widgetFilters: Record<string, string | string[]> = {};
  const tz = dateRange?.timezone || 'UTC';
  // Stored filters first, header date range last — the dashboard-level date
  // range picker is always authoritative. Stripping start/end keys from the
  // stored payload would also work, but re-stamping after the merge keeps
  // the special-cased keys in one place and survives any future stored-key
  // shape drift.
  const storedFilters = (widget.widget_filters_json ?? {}) as Record<
    string,
    string | string[]
  >;
  Object.assign(widgetFilters, storedFilters);
  if (dateRange?.startDate) {
    widgetFilters.start_date = addTimezoneStartOfDay(dateRange.startDate, tz);
  }
  if (dateRange?.endDate) {
    widgetFilters.end_date = addTimezoneEndOfDay(dateRange.endDate, tz);
  }

  const widgetConfig: Record<string, unknown> = {
    ...((widget.widget_config_json ?? {}) as Record<string, unknown>),
  };
  // Tables need pagination on the very first request — ByodTable skips its
  // initial-mount onFetchData call (didMountRef), so the runtime offset/
  // limit aren't stamped into widget_config_json until the user changes
  // page or sort. Without these defaults, the first /query goes out with
  // no offset/limit and the backend has to assume. Stamp at the API
  // boundary so stored state stays clean (no zero-value pollution into
  // widget_config_json that would round-trip on save).
  if (widget.widget_type === WIDGET_TYPE.TABLE) {
    if (widgetConfig.limit == null) {
      widgetConfig.limit = DEFAULT_PAGE_SIZE;
    }
    if (widgetConfig.offset == null) {
      widgetConfig.offset = 0;
    }
  }

  return {
    placement_id: placementId,
    widget_key: widget.widget_key,
    widget_filters: widgetFilters,
    widget_config: widgetConfig,
  };
}

/**
 * Per-tile data fetch gated by viewport visibility.
 *
 * Fires a query for this placement only when the attached element has been
 * continuously visible for 200ms (via `useInViewport`). In-flight
 * requests are cancelled if the tile scrolls out of view before the response
 * arrives; successfully loaded data is cached indefinitely until explicitly
 * invalidated (date range / filter / config change).
 */
export function useTileData(
  placementId: string,
  options?: UseTileDataOptions,
): UseTileDataResult {
  const retryCount = options?.retry ?? DEFAULT_TILE_RETRY;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {ref, isInViewport} = useInViewport({
    settleMs: 100,
    rootMargin: '200px 0px',
  });

  // Reactive reads — queryKey must re-hash when these change so React Query
  // treats them as new queries (auto-refetch on next enabled render).
  const dashboardId = useDashboardStore((s) => s.id);
  const widget = useDashboardStore((s) => s.widgets[placementId]);
  // dateRange lives in the URL via useQueryObject — same hook used by
  // DashboardBuilder's picker and the edit panel, so all consumers share
  // a single source of truth without store/context plumbing.
  const [dateRange] = useByodDateRange();

  const widgetFiltersKey = JSON.stringify(widget?.widget_filters_json ?? null);
  const widgetConfigKey = JSON.stringify(widget?.widget_config_json ?? null);
  const dateRangeKey = dateRange
    ? `${dateRange.startDate}|${dateRange.endDate}|${dateRange.timezone}`
    : '';

  const queryKey = React.useMemo(
    () => [
      BYOD_TILE_QUERY_KEY,
      placementId,
      dashboardId,
      dateRangeKey,
      widgetFiltersKey,
      widgetConfigKey,
    ],
    [placementId, dashboardId, dateRangeKey, widgetFiltersKey, widgetConfigKey],
  );

  // Once a fetch sequence terminally fails for a given queryKey, suppress
  // future auto-fetches for that same key — the tile has already exhausted
  // its retry budget and re-firing on every viewport in/out just hammers a
  // backend we already know is rejecting this request. Any queryKey change
  // (filters, date range, widget config, dashboard) lands on a different
  // hash and clears the lock; the manual retry button also clears it. The
  // ref lives in component state so a remount (route away and back) starts
  // fresh — a deliberate page navigation buys one new attempt.
  const failedKeyHashRef = React.useRef<string | null>(null);
  const queryKeyHash = `${placementId}|${dashboardId}|${dateRangeKey}|${widgetFiltersKey}|${widgetConfigKey}`;
  const isLocked = failedKeyHashRef.current === queryKeyHash;

  // Suppress fetching when this tile is being rendered inside a drag
  // overlay — the overlay clone shares its placementId with an already-
  // mounted tile in the canvas, so the cache already holds (or is fetching)
  // exactly this data. With `enabled: false`, useQuery still subscribes to
  // the cache and surfaces whatever's there via `query.data`, so the
  // overlay shows the live chart/table without firing a duplicate /query.
  const isDragOverlay = React.useContext(ByodDragOverlayContext);

  // `dashboardId` may be null during client-only draft state; `/query` is
  // stateless and does not require a real dashboard. Only widget presence
  // is required. `dateRange` is always populated (useByodDateRange returns
  // a non-null default when the URL slot is empty), so no dateRange gate
  // is needed.
  const enabled =
    isInViewport && Boolean(widget) && !isLocked && !isDragOverlay;

  const query = useQuery<PreviewResponse, Error>({
    queryKey,
    enabled,
    // Cached data is considered fresh for 5 minutes. Within that window
    // remounts and window refocuses reuse the cache (no redundant fetches
    // on edit/view toggle, route-in/out, etc.). After 5 min, the next
    // mount or focus event triggers a background refetch so a user who
    // left the dashboard open for hours sees current data on return.
    staleTime: 5 * 60 * 1000,
    // Keep the previous response visible while a new query (filter, page,
    // sort, date) is in flight. Without this, queryKey changes blank out
    // `data` and the table footer loses totalCount / pagination metadata
    // until the next response arrives — `isFetching` already drives the
    // loading indicator inside ByodTable, so the brief stale window is
    // strictly better UX than empty state flicker.
    placeholderData: keepPreviousData,
    retry: (failureCount, error) =>
      failureCount < retryCount && isByodErrorRetryable(error),
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    queryFn: async ({signal}) => {
      const input = buildWidgetInput(placementId, widget, dateRange);
      if (!input) {
        // Store mutation race: widget removed between enable check and fetch.
        // Retrying won't reseed the store — fail fast and observe.
        const err = new BYODTileError('Widget not found in store', {
          retryable: false,
        });
        try {
          reportByodApiError(err, {action: 'tile-query', placementId});
        } catch (reportErr) {
          logger.debug('reportByodApiError threw while reporting tile error', {
            placementId,
            reportErr,
          });
        }
        throw err;
      }
      const response = (await dispatch(
        baseRequest(`${BASE}/query`, {
          method: 'POST',
          data: {widgets: [input]},
          signal,
          options: {handleAuth: true},
        }),
      )) as DashboardQueryResponse;
      const result = response.widgets[0];
      if (!result) {
        // Backend contract violation — empty widgets array. Worth a retry
        // (transient) and worth reporting either way.
        const err = new BYODTileError('Empty response from /query', {
          retryable: true,
        });
        try {
          reportByodApiError(err, {action: 'tile-query', placementId});
        } catch (reportErr) {
          logger.debug('reportByodApiError threw while reporting tile error', {
            placementId,
            reportErr,
          });
        }
        throw err;
      }
      if (result.status === 'error') {
        // Backend-reported tile-level error. Don't retry: the backend has
        // already rejected this input shape; another attempt will repeat it.
        throw new BYODTileError(result.error || 'Tile query failed', {
          retryable: false,
        });
      }
      // Strip the wrapper (placement_id, status) — tile only needs the payload.
      const {placement_id: _p, status: _s, ...payload} = result;
      return payload as PreviewResponse;
    },
  });

  // Cancel in-flight request when the tile scrolls out of view mid-load.
  // Without this, React Query would let the request complete (wasted network).
  // Only fires on the true→false transition — otherwise a new queryKey (from a
  // filter/config change) while still off-screen would also trigger a no-op
  // cancel every render.
  // Cleanup cancels with the *captured* queryKey so that a key change mid-
  // flight (e.g. filter edit while fetching) also aborts the old request
  // instead of leaking it to completion under the now-orphaned key.
  const prevIsVisibleRef = React.useRef(isInViewport);
  React.useEffect(() => {
    if (prevIsVisibleRef.current && !isInViewport) {
      void queryClient.cancelQueries({queryKey});
    }
    prevIsVisibleRef.current = isInViewport;
    return () => {
      void queryClient.cancelQueries({queryKey});
    };
  }, [isInViewport, queryClient, queryKey]);

  // Lock only when a fetch attempted *in the current view of this queryKey*
  // ends with error. We detect this via a fetchStatus 'fetching' → non-fetching
  // transition while `isError` is true.
  //
  // Naive guard ("if isError, lock") fires on cached-error revisits too: when
  // the user goes A (errored) → B → back to A, A's error sits in React Query's
  // cache, `query.isError` is true on the first render after revisit, and we'd
  // re-lock A even though the user just navigated to it intentionally. The
  // transition-based guard ignores that initial cached-error render and only
  // engages when a fresh fetch we observed actually fails — which is what
  // "queryKey change clears the lock" was meant to mean.
  const wasFetchingRef = React.useRef(false);

  // Reset the lock whenever the queryKey changes — a different filter / date /
  // dashboard is logically a new fetch, so any prior failure for the old key
  // shouldn't carry over.
  React.useEffect(() => {
    failedKeyHashRef.current = null;
    wasFetchingRef.current = false;
  }, [queryKeyHash]);

  React.useEffect(() => {
    const wasFetching = wasFetchingRef.current;
    const isFetching = query.fetchStatus === 'fetching';
    if (wasFetching && !isFetching && query.isError) {
      failedKeyHashRef.current = queryKeyHash;
    }
    wasFetchingRef.current = isFetching;
  }, [query.fetchStatus, query.isError, queryKeyHash]);

  // Capture `query.refetch` via a ref so the exposed `refetch` callback is
  // referentially stable across renders. React Query returns a fresh `query`
  // object each render, so a `useCallback(..., [query])` would itself be
  // unstable — fine for `onClick={refetch}`, but a `useEffect(..., [refetch])`
  // consumer would loop. The ref lets callers depend on `refetch` safely.
  const queryRefetchRef = React.useRef(query.refetch);
  queryRefetchRef.current = query.refetch;
  const refetch = React.useCallback(() => {
    failedKeyHashRef.current = null;
    void queryRefetchRef.current();
  }, []);

  return {
    ref,
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    fetchStatus: query.fetchStatus,
    refetch,
  };
}
